import { Avatar, UserPill, UserPillColorEnum } from '@acceso/design-system';
import { useMemo } from 'react';

import { useUser } from '@/features/users/api/get-user';
import { UUID } from '@/types';
import { toAppWallClock } from '@/utils/spain-time';

export interface UserActionProps {
  id?: UUID;
  timestamp?: string | Date;
  hideTimestamp?: boolean;
  isSystem?: boolean;
  isAvatar?: boolean;
  formatZ?: string;
}

export function UserAction({
  id = '',
  isSystem = false,
  timestamp,
  isAvatar = false,
  hideTimestamp = false,
  formatZ,
}: UserActionProps) {
  const { data } = useUser({
    id,
  });

  const user = useMemo(() => {
    if (data) {
      return data;
    }
    if (isAvatar) {
      return {
        id,
      };
    }
    return {};
  }, [id, data, isAvatar]);

  // `UserPill` formats the timestamp — or, without one, the user's
  // `created_at` — in the browser's clock. It is handed a Date whose
  // browser-clock fields spell the Madrid wall clock instead, so the date reads
  // in Spanish time for every viewer (RCOMM-6264). For display only.
  const pillTimestamp = useMemo(() => {
    const value = timestamp || data?.created_at;

    return value ? toAppWallClock(value) : timestamp;
  }, [timestamp, data?.created_at]);

  return isAvatar ? (
    <Avatar user={user} className="select-none" />
  ) : (
    <UserPill
      className="select-none"
      color={UserPillColorEnum.DEFAULT}
      user={user}
      timestamp={pillTimestamp}
      hideTimestamp={hideTimestamp}
      formatZ={formatZ}
      isSystem={isSystem}
    />
  );
}
